import React, { useEffect, useRef, useState } from 'react';
import ChevronDown from 'assets/chevron_down.svg';
import { Formik } from 'formik';
import {
  Button,
  ButtonType,
  Drawer,
  Select,
  Spin
} from 'syngenta-digital-cropwise-react-ui-kit';
import usePeopleData from 'hooks/usePeopleData';
import useCompanies from 'hooks/useCompanies';
import Close from 'assets/close.svg';
import focusInput from 'screens/Applications&WorkOrders/StepsLayout/Helpers/focusInput';
import AddPerson from './components/AddPerson';
import AddCompany from './components/AddCompany';
import { AddNewCompanyPeopleDrawerProps } from '../../types';
import './index.css';

const AddNewCompanyPeopleDrawer: React.FC<AddNewCompanyPeopleDrawerProps> = ({
  open,
  onClose,
  onCompanyPeopleAdded,
  title,
  defaultType = 'Company',
  disableTypeChange
}) => {
  const [
    companyPeopleTypeSelectOpen,
    setCompanyPeopleTypeSelectOpen
  ] = useState(false);
  const [companyPeopleType, setCompanyPeopleType] = useState(defaultType);
  const drawerContentRef = useRef<HTMLDivElement | null>(null);

  const { createPerson, isCreatingPerson } = usePeopleData();

  const { createCompany, isCreatingCompany } = useCompanies();

  const onSubmit = (v: any, { resetForm }: any) => {
    if (companyPeopleType === 'People') {
      const personBody = {
        name: v.name,
        email: v.email,
        phone: v.phone,
        companyId: v.company,
        address: {
          line1: v.addressLineOne,
          line2: v.addressLineTwo,
          city: v.city,
          state: v.state,
          zipCode: v.zipCode,
          country: v.country
        },
        applicatorLicenseNumber: v.applicatorLicenseNumber,
        applicatorLicenseExpirationDateEpoch: v.applicatorLicenseExpirationDate
          ? v.applicatorLicenseExpirationDate.valueOf?.() ??
            Date.parse(v.applicatorLicenseExpirationDate)
          : '',
        notes: v.notes
      };
      createPerson(personBody).then(({ id, name }) => {
        if (id && name) {
          onCompanyPeopleAdded({
            type: 'People',
            id,
            name,
            licenseNumber: v.applicatorLicenseNumber,
            isNew: true
          });
          resetForm();
          setCompanyPeopleType(defaultType);
          onClose();
        }
      });
    } else {
      const companyBody = {
        name: v.name,
        email: v.email?.trim(),
        legalName: v.legalName,
        phone: v.phone,
        address: {
          line1: v.addressLineOne,
          line2: v.addressLineTwo,
          city: v.city,
          state: v.state,
          zipCode: v.zipCode,
          country: v.country
        },
        notes: v.notes
      };
      createCompany(companyBody).then(({ id, name }) => {
        if (id && name) {
          onCompanyPeopleAdded({ type: 'Company', id, name, isNew: true });
          resetForm();
          setCompanyPeopleType(defaultType);
          onClose();
        }
      });
    }
  };

  const isSaveButtonDisabled = ({
    isValid,
    dirty
  }: {
    isValid: boolean;
    dirty: boolean;
  }) => !isValid || !dirty || isCreatingPerson || isCreatingCompany;

  const initialValues = {
    name: '',
    legalName: '',
    email: '',
    phone: '',
    applicatorLicenseNumber: '',
    applicatorLicenseExpirationDate: null,
    notes: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    state: null,
    zipCode: ''
  };

  const type = companyPeopleType === 'People' ? 'Person' : 'Company';
  const buttonText = `Save ${type}`;

  useEffect(() => {
    if (!open) {
      return;
    }

    focusInput(drawerContentRef, 'input[name="name"]');
  }, [companyPeopleType, isCreatingCompany, isCreatingPerson, open]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validate={(values: typeof initialValues) => {
        const errors: Record<string, string> = {};
        const regex = /^\s+$/;
        if (!values.name || regex.test(values.name)) {
          errors.name = 'Name is required';
        }
        return errors;
      }}
      validateOnMount
      onSubmit={onSubmit}
    >
      {({
        isValid,
        dirty,
        submitForm,
        resetForm,
        errors,
        touched,
        handleChange,
        handleBlur,
        values
      }) => {
        return (
          <Drawer
            maskClosable
            open={open}
            className="cwf-add-company-people-drawer"
            placement="right"
            closable={false}
            onClose={() => {
              resetForm();
              onClose();
            }}
            bodyStyle={{ padding: 0 }}
            footerStyle={{ borderTop: '1px solid #dfe2e7' }}
            footer={
              <div className="flex justify-between">
                <Button
                  type={ButtonType.outline}
                  onClick={() => {
                    resetForm({ values: initialValues });
                    setCompanyPeopleType('Company');
                    onClose();
                  }}
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  disabled={!!isSaveButtonDisabled({ isValid, dirty })}
                  className="company-people-add-save-button"
                  type={ButtonType.primary}
                  onClick={submitForm}
                  size="large"
                >
                  {buttonText}
                </Button>
              </div>
            }
            width="696px"
          >
            <div className="relative">
              <div className="absolute top-0 right-0 m-3">
                <button
                  type="button"
                  data-testid="close-form"
                  onClick={() => {
                    resetForm({ values: initialValues });
                    onClose();
                  }}
                >
                  <img src={Close} alt="Close icon" width={20} height={20} />
                </button>
              </div>
            </div>
            <div className="px-8 py-6" ref={drawerContentRef}>
              <div
                className="flex gap-4 items-center mb-6"
                data-testid="company-people-container"
              >
                <h1 className="text-xl font-semibold block mb-0 mt-2">
                  {title}
                </h1>
                {disableTypeChange ? null : (
                  <Select
                    className="w-140px"
                    data-testid="company-people-select"
                    id="company-people-select"
                    onChange={value => {
                      setCompanyPeopleType(value);
                      resetForm({ values: initialValues });
                    }}
                    value={companyPeopleType}
                    onDropdownVisibleChange={(selectOpen: boolean) =>
                      setCompanyPeopleTypeSelectOpen(selectOpen)
                    }
                    getPopupContainer={triggerNode =>
                      triggerNode.parentElement || document.body
                    }
                    suffixIcon={
                      <img
                        src={ChevronDown}
                        style={{
                          transform: companyPeopleTypeSelectOpen
                            ? 'rotate(180deg)'
                            : 'none'
                        }}
                        height={18}
                        width={18}
                        alt="Select dropdown icon"
                      />
                    }
                  >
                    <Select.Option value="People" data-testid="select-option1">
                      Person
                    </Select.Option>
                    <Select.Option value="Company">
                      <div data-testid="select-option2">Company</div>
                    </Select.Option>
                  </Select>
                )}
              </div>
              {isCreatingPerson || isCreatingCompany ? (
                <div className="flex items-center justify-center h-100">
                  <Spin />
                </div>
              ) : (
                <div className="mb-8">
                  {companyPeopleType === 'People' ? (
                    <AddPerson
                      values={values}
                      onChanges={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                    />
                  ) : (
                    <AddCompany
                      values={values}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>
              )}
            </div>
          </Drawer>
        );
      }}
    </Formik>
  );
};

export default AddNewCompanyPeopleDrawer;
